/**
 * 甘特图任务列表组件
 *
 * 显示左侧固定的任务列表，支持分组和折叠
 */

import { ChevronDown, ChevronRight } from 'lucide-react'
import * as React from 'react'

import { PriorityBadge } from '@/components/common/priority-badge'
import { cn } from '@/lib/utils'

import { DEFAULT_ROW_HEIGHT, TASK_LIST_WIDTH } from './types'

import type { GanttTask, TaskGroup, GroupByOption } from './types'

interface TaskListProps {
  /** 任务列表 */
  tasks: GanttTask[]
  /** 分组数据 */
  groups: TaskGroup[]
  /** 分组方式 */
  groupBy: GroupByOption
  /** 任务点击回调 */
  onTaskClick: (taskId: string) => void
  /** 分组展开/折叠回调 */
  onToggleGroup: (groupId: string) => void
  /** 行高 */
  rowHeight?: number
  /** 滚动位置同步 */
  scrollTop: number
  /** 滚动回调 */
  onScroll: (scrollTop: number) => void
}

export function TaskList({
  tasks,
  groups,
  groupBy,
  onTaskClick,
  onToggleGroup,
  rowHeight = DEFAULT_ROW_HEIGHT,
  scrollTop,
  onScroll,
}: TaskListProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  // 处理滚动
  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      onScroll(e.currentTarget.scrollTop)
    },
    [onScroll]
  )

  // 同步滚动位置
  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollTop
    }
  }, [scrollTop])

  return (
    <div className="bg-background flex flex-col border-r" style={{ width: TASK_LIST_WIDTH }}>
      {/* 表头 */}
      <div className="bg-background text-muted-foreground sticky top-0 z-10 flex h-10 items-center border-b px-3 text-sm font-medium">
        任务
      </div>

      {/* 任务列表 */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        onScroll={handleScroll}
      >
        {groupBy === 'none' ? (
          // 无分组模式
          <div>
            {tasks.map((task) => (
              <TaskListItem
                key={task.id}
                task={task}
                rowHeight={rowHeight}
                onClick={() => onTaskClick(task.id)}
              />
            ))}
          </div>
        ) : (
          // 分组模式
          <div>
            {groups.map((group) => (
              <TaskGroupSection
                key={group.id}
                group={group}
                rowHeight={rowHeight}
                onToggle={() => onToggleGroup(group.id)}
                onTaskClick={onTaskClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== 子组件 ====================

interface TaskListItemProps {
  task: GanttTask
  rowHeight: number
  onClick: () => void
  indent?: number
}

function TaskListItem({ task, rowHeight, onClick, indent = 0 }: TaskListItemProps) {
  return (
    <div
      className={cn(
        'hover:bg-accent/50 flex cursor-pointer items-center gap-2 border-b px-3',
        task.isOverdue && 'bg-red-50 dark:bg-red-950/20'
      )}
      style={{ height: rowHeight, paddingLeft: 12 + indent * 16 }}
      onClick={onClick}
    >
      {/* 优先级指示器 */}
      <div className="flex-shrink-0">
        <PriorityBadge priority={task.priority} compact showIcon={false} className="px-1.5" />
      </div>

      {/* 任务编号和标题 */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="text-muted-foreground flex-shrink-0 text-xs">{task.code}</span>
        <span className="truncate text-sm">{task.title}</span>
      </div>

      {/* 状态标签 */}
      <TaskStatusBadge status={task.status} />
    </div>
  )
}

interface TaskGroupSectionProps {
  group: TaskGroup
  rowHeight: number
  onToggle: () => void
  onTaskClick: (taskId: string) => void
}

function TaskGroupSection({ group, rowHeight, onToggle, onTaskClick }: TaskGroupSectionProps) {
  return (
    <div>
      {/* 分组头部 */}
      <div
        className="bg-muted/50 hover:bg-muted flex cursor-pointer items-center gap-2 border-b px-3"
        style={{ height: rowHeight }}
        onClick={onToggle}
      >
        {group.isExpanded ? (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronRight className="text-muted-foreground h-4 w-4" />
        )}
        <span className="flex-1 truncate text-sm font-medium">{group.name}</span>
        <span className="text-muted-foreground text-xs">{group.tasks.length}</span>
      </div>

      {/* 分组内的任务 */}
      {group.isExpanded && (
        <div>
          {group.tasks.map((task) => (
            <TaskListItem
              key={task.id}
              task={task}
              rowHeight={rowHeight}
              onClick={() => onTaskClick(task.id)}
              indent={1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface TaskStatusBadgeProps {
  status: GanttTask['status']
}

function TaskStatusBadge({ status }: TaskStatusBadgeProps) {
  const statusConfig: Record<GanttTask['status'], { label: string; className: string }> = {
    backlog: {
      label: '待办',
      className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    },
    todo: {
      label: '待开始',
      className: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
    },
    in_progress: {
      label: '进行中',
      className: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400',
    },
    code_review: {
      label: '代码审查',
      className: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400',
    },
    testing: {
      label: '测试中',
      className: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400',
    },
    done: {
      label: '已完成',
      className: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400',
    },
    blocked: {
      label: '已阻塞',
      className: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400',
    },
    cancelled: {
      label: '已取消',
      className: 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500',
    },
  }

  const config = statusConfig[status]

  return (
    <span className={cn('flex-shrink-0 rounded px-1.5 py-0.5 text-xs', config.className)}>
      {config.label}
    </span>
  )
}
